/**
 * Settings Page
 *
 * App settings and content filter configuration.
 */

import { useState, useEffect } from 'react';
import { useWhitelistStore } from '@/stores/whitelist';
import { PageHeader } from '@/components/common/PageHeader';
import { api } from '@/api/client';

interface YouTubeAuthStatus {
  authenticated: boolean;
  hasVisitorData: boolean;
  hasDataSyncId: boolean;
  expiresAt?: number;
}

export function SettingsPage() {
  const { filterConfig, setFilterConfig, syncWhitelist, isLoading } = useWhitelistStore();

  // YouTube auth state
  const [ytAuthStatus, setYtAuthStatus] = useState<YouTubeAuthStatus | null>(null);
  const [ytAuthLoading, setYtAuthLoading] = useState(false);
  const [ytAuthError, setYtAuthError] = useState<string | null>(null);
  const [showManualImport, setShowManualImport] = useState(false);
  const [manualCookies, setManualCookies] = useState('');

  // Check YouTube auth status on mount
  useEffect(() => {
    checkYouTubeAuthStatus();
  }, []);

  const checkYouTubeAuthStatus = async () => {
    try {
      const response = await api.authStatus();
      if (response.success) {
        setYtAuthStatus(response as unknown as YouTubeAuthStatus);
      }
    } catch (error) {
      console.error('Failed to check YouTube auth status:', error);
    }
  };

  const handleYouTubeLogin = async () => {
    setYtAuthLoading(true);
    setYtAuthError(null);
    try {
      const response = await api.authLogin();
      if (response.success) {
        setYtAuthStatus({
          authenticated: true,
          hasVisitorData: (response as any).hasVisitorData,
          hasDataSyncId: (response as any).hasDataSyncId,
        });
      } else {
        setYtAuthError(response.error?.message || 'Login failed');
      }
    } catch (error) {
      setYtAuthError('Failed to connect to server');
    } finally {
      setYtAuthLoading(false);
    }
  };

  const handleYouTubeLogout = async () => {
    setYtAuthLoading(true);
    try {
      await api.authLogout();
      setYtAuthStatus({ authenticated: false, hasVisitorData: false, hasDataSyncId: false });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setYtAuthLoading(false);
    }
  };

  const handleManualImport = async () => {
    if (!manualCookies.trim()) {
      setYtAuthError('Please paste your cookies');
      return;
    }

    setYtAuthLoading(true);
    setYtAuthError(null);
    try {
      const response = await api.post<any>('/auth/import', { cookies: manualCookies.trim() });
      if (response.success) {
        setYtAuthStatus({
          authenticated: true,
          hasVisitorData: response.data?.hasVisitorData,
          hasDataSyncId: false,
        });
        setShowManualImport(false);
        setManualCookies('');
      } else {
        setYtAuthError(response.error?.message || 'Import failed');
      }
    } catch (error) {
      setYtAuthError('Failed to import cookies');
    } finally {
      setYtAuthLoading(false);
    }
  };

  return (
    <div className="min-h-full">
      <PageHeader title="Settings" />

      <div className="px-4 pb-8 space-y-6">
        {/* YouTube Music Authentication */}
        <section className="card">
          <h2 className="text-lg font-bold mb-4">YouTube Music</h2>
          <p className="text-sm text-on-surface-variant mb-4">
            Sign in with your YouTube account to enable music streaming
          </p>
          {ytAuthStatus?.authenticated ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-on-surface">Connected to YouTube Music</span>
              </div>
              <div className="text-xs text-on-surface-variant space-y-1">
                <p>Visitor Data: {ytAuthStatus.hasVisitorData ? 'Yes' : 'No'}</p>
                <p>Data Sync: {ytAuthStatus.hasDataSyncId ? 'Yes' : 'No'}</p>
              </div>
              <button
                className="btn-secondary w-full"
                onClick={handleYouTubeLogout}
                disabled={ytAuthLoading}
              >
                {ytAuthLoading ? 'Disconnecting...' : 'Disconnect YouTube'}
              </button>
            </div>
          ) : showManualImport ? (
            <div className="space-y-3">
              <p className="text-sm text-on-surface-variant">
                1. Open <a href="https://music.youtube.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">music.youtube.com</a> in your browser and sign in
              </p>
              <p className="text-sm text-on-surface-variant">
                2. Press F12, go to Application &gt; Cookies, copy all cookie values
              </p>
              <p className="text-sm text-on-surface-variant">
                3. Paste below in format: SAPISID=xxx; SID=xxx; HSID=xxx; ...
              </p>
              <textarea
                className="w-full h-24 p-2 bg-surface-container rounded-lg text-sm font-mono"
                placeholder="SAPISID=xxx; SID=xxx; HSID=xxx; ..."
                value={manualCookies}
                onChange={(e) => setManualCookies(e.target.value)}
              />
              <div className="flex gap-2">
                <button
                  className="btn-secondary flex-1"
                  onClick={() => setShowManualImport(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary flex-1"
                  onClick={handleManualImport}
                  disabled={ytAuthLoading}
                >
                  {ytAuthLoading ? 'Importing...' : 'Import Cookies'}
                </button>
              </div>
              {ytAuthError && (
                <p className="text-error text-sm text-center">{ytAuthError}</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <button
                className="btn-primary w-full flex items-center justify-center gap-2"
                onClick={handleYouTubeLogin}
                disabled={ytAuthLoading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.5 6.2c-.3-1-1-1.8-2-2.1C19.8 3.5 12 3.5 12 3.5s-7.8 0-9.5.6c-1 .3-1.7 1.1-2 2.1-.6 1.7-.6 5.3-.6 5.3s0 3.6.6 5.3c.3 1 1 1.8 2 2.1 1.7.6 9.5.6 9.5.6s7.8 0 9.5-.6c1-.3 1.7-1.1 2-2.1.6-1.7.6-5.3.6-5.3s0-3.6-.6-5.3zM9.5 15.5v-7l6.3 3.5-6.3 3.5z"/>
                </svg>
                {ytAuthLoading ? 'Opening browser...' : 'Connect YouTube Account'}
              </button>
              {ytAuthError && (
                <p className="text-error text-sm text-center">{ytAuthError}</p>
              )}
              <p className="text-xs text-on-surface-variant text-center">
                A browser window will open for you to sign in to Google
              </p>
              <button
                className="text-sm text-primary underline w-full text-center"
                onClick={() => setShowManualImport(true)}
              >
                Having trouble? Import cookies manually
              </button>
            </div>
          )}
        </section>

        {/* Content Filters */}
        <section className="card">
          <h2 className="text-lg font-bold mb-4">Content Filters</h2>
          <div className="space-y-4">
            {/* Filters enabled toggle */}
            <label className="flex items-center justify-between">
              <span>Enable content filters</span>
              <input
                type="checkbox"
                checked={filterConfig.filtersEnabled}
                onChange={(e) => setFilterConfig({ filtersEnabled: e.target.checked })}
                className="w-5 h-5 accent-primary"
              />
            </label>

            {filterConfig.filtersEnabled && (
              <>
                <label className="flex items-center justify-between">
                  <span>Allow female singers</span>
                  <input
                    type="checkbox"
                    checked={filterConfig.allowFemaleSingers}
                    onChange={(e) => setFilterConfig({ allowFemaleSingers: e.target.checked })}
                    className="w-5 h-5 accent-primary"
                  />
                </label>

                <label className="flex items-center justify-between">
                  <span>Allow Kid Zone content</span>
                  <input
                    type="checkbox"
                    checked={filterConfig.allowKidZone}
                    onChange={(e) => setFilterConfig({ allowKidZone: e.target.checked })}
                    className="w-5 h-5 accent-primary"
                  />
                </label>

                <label className="flex items-center justify-between">
                  <span>Allow Israeli artists</span>
                  <input
                    type="checkbox"
                    checked={filterConfig.allowIsraeli}
                    onChange={(e) => setFilterConfig({ allowIsraeli: e.target.checked })}
                    className="w-5 h-5 accent-primary"
                  />
                </label>
              </>
            )}
          </div>
        </section>

        {/* Sync */}
        <section className="card">
          <h2 className="text-lg font-bold mb-4">Data</h2>
          <button
            className="btn-secondary w-full"
            onClick={syncWhitelist}
            disabled={isLoading}
          >
            {isLoading ? 'Syncing...' : 'Sync Whitelist'}
          </button>
        </section>

        {/* About */}
        <section className="card">
          <h2 className="text-lg font-bold mb-4">About</h2>
          <div className="space-y-2 text-sm text-on-surface-variant">
            <p>Zemer Music v0.1.0</p>
            <p>Built with love for the community</p>
          </div>
        </section>
      </div>
    </div>
  );
}
